import { useState, useCallback, useMemo } from "react";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";

enum SurveyReason {
    Remote = "remote",
    LiveStream = "live-stream",
    Other = "other"
}

enum Screen {
    Survey = "survey",
    Redirect = "redirect"
}

const SURVEY_OPTIONS: { value: SurveyReason; label: string }[] = [
    {
        value: SurveyReason.Remote,
        label: "I want to deploy in a remote area without internet connection"
    },
    { value: SurveyReason.LiveStream, label: "I want to run on a live stream" },
    { value: SurveyReason.Other, label: "Other" }
];

interface FeatureNotAvailableModalProps {
    isOpen: boolean;
    /** Dismissed from the survey screen without submitting — caller should reopen the deploy modal */
    onDismiss: () => void;
    /** Completed the flow (survey submitted) — caller should NOT reopen the deploy modal */
    onComplete: () => void;
    /** "Start Deploying" on the RTSP redirect screen — caller should reopen deploy modal with Video -> API */
    onStartRTSP: () => void;
}

function StreamingRedirectContent({ onStartDeploying }: { onStartDeploying: () => void }) {
    return (
        <>
            <p className="text-center text-sm text-gray-600">
                For live streaming, you don&apos;t need local deployment. Use our Streaming API
                today for low-latency, real-time video inference.
            </p>

            <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-start gap-3">
                    <i className="far fa-cctv mt-0.5 text-lg text-gray-500" />
                    <div>
                        <p className="text-sm font-medium text-gray-900">
                            Use the Video Streaming API
                        </p>
                        <p className="text-xs text-gray-500">
                            Run live video inference in the cloud and receive results in real-time.
                        </p>
                    </div>
                </div>
            </div>

            <button
                className="w-full rounded-lg bg-purple-600 px-4 py-3 text-sm font-medium text-white hover:bg-purple-700"
                onClick={onStartDeploying}
            >
                Start Deploying
            </button>
        </>
    );
}

function SurveyContent({
    selectedOption,
    onSelectOption,
    otherText,
    onOtherTextChange,
    onSubmit
}: {
    selectedOption: SurveyReason | null;
    onSelectOption: (value: SurveyReason) => void;
    otherText: string;
    onOtherTextChange: (text: string) => void;
    onSubmit: () => void;
}) {
    return (
        <>
            <p className="text-center text-sm text-gray-600">
                This feature isn&apos;t available yet, but we&apos;d love to learn more about your
                use case.
            </p>

            <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-gray-700">
                    Why do you want to run locally on your device?
                </p>
                {SURVEY_OPTIONS.map((option) => (
                    <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-2 text-sm text-gray-600"
                    >
                        <input
                            type="radio"
                            name="local-deploy-reason"
                            value={option.value}
                            checked={selectedOption === option.value}
                            onChange={() => onSelectOption(option.value)}
                            className="text-purple-600 focus:ring-purple-500"
                        />
                        {option.label}
                    </label>
                ))}

                {selectedOption === SurveyReason.Other && (
                    <input
                        type="text"
                        value={otherText}
                        onChange={(e) => onOtherTextChange(e.target.value)}
                        placeholder="Enter reason"
                        className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                )}
            </div>

            <button
                className="w-full rounded-lg bg-purple-600 px-4 py-3 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onSubmit}
                disabled={!selectedOption}
            >
                Submit
            </button>
        </>
    );
}

const SCREEN_CONFIG = {
    [Screen.Survey]: {
        type: "info" as const,
        icon: "far fa-server",
        title: "Feature Not Available"
    },
    [Screen.Redirect]: {
        type: "success" as const,
        icon: "far fa-check",
        title: "Good news! Live streaming is available"
    }
};

export function FeatureNotAvailableModal({
    isOpen,
    onDismiss,
    onComplete,
    onStartRTSP
}: FeatureNotAvailableModalProps) {
    const [screen, setScreen] = useState<Screen>(Screen.Survey);
    const [selectedOption, setSelectedOption] = useState<SurveyReason | null>(null);
    const [otherText, setOtherText] = useState("");
    const { track } = useAnalytics();
    const { task } = useFlow();

    const resetState = useCallback(() => {
        setScreen(Screen.Survey);
        setSelectedOption(null);
        setOtherText("");
    }, []);

    const handleClose = useCallback(() => {
        const wasSurvey = screen === Screen.Survey;
        resetState();
        if (wasSurvey) {
            onDismiss();
        } else {
            onComplete();
        }
    }, [screen, resetState, onDismiss, onComplete]);

    const handleSubmit = useCallback(() => {
        track({
            event: AnalyticsFlowEvent.LOCAL_DEPLOY_SURVEY_SUBMITTED,
            data: {
                task,
                reason: selectedOption,
                otherText: selectedOption === SurveyReason.Other ? otherText : undefined
            }
        });

        if (selectedOption === SurveyReason.LiveStream) {
            setScreen(Screen.Redirect);
        } else {
            resetState();
            onComplete();
        }
    }, [track, task, selectedOption, otherText, resetState, onComplete]);

    const handleStartDeploying = useCallback(() => {
        resetState();
        onStartRTSP();
    }, [resetState, onStartRTSP]);

    const { type, icon, title } = useMemo(() => SCREEN_CONFIG[screen], [screen]);

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={handleClose}
            type={type}
            icon={icon}
            title={title}
            size="sm"
            showFooter={false}
        >
            {screen === Screen.Survey ? (
                <SurveyContent
                    selectedOption={selectedOption}
                    onSelectOption={setSelectedOption}
                    otherText={otherText}
                    onOtherTextChange={setOtherText}
                    onSubmit={handleSubmit}
                />
            ) : (
                <StreamingRedirectContent onStartDeploying={handleStartDeploying} />
            )}
        </ModalWithIcon>
    );
}
